import { useCallback, useMemo } from 'react';

import { colors } from '@/assets/styles/colors';
import { ROUTE_DEFINITIONS } from '@/routes';
import type { TableAction, TableColumn } from '@/components/ui/tables/Table.types';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import { recalculationMonthlyCutsService } from '@/api/domains/recalculation';
import type { ProcessHistoryItem, ProcessHistoryFilters, RecalculoProcessDetail } from '@/api/domains/recalculation';
import { useUI } from '@/hooks/ui/useUI';
import { useExport } from '@/hooks/ui/useExport';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useTranslation } from '@/i18n';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import {
  PENDING_STATUS,
  IN_PROGRESS_STATUS,
  ERROR_STATUS,
  FINISHED_STATUS,
  STOPPED_STATUS,
} from '@/hooks/attendance/recalculation/useRecalculationStatus';


const ORDER_BY_MAP: Record<string, string> = {
  processId: 'processId',
  cutYear: 'cutYear',
  cutMonth: 'cutMonth',
  totalEmployees: 'totalEmployees',
  processedEmployees: 'processedEmployees',
  executionDate: 'executionDate',
  executedBy: 'executedBy',
  status: 'status',
};

const STATUS_COLOR_MAP: Record<string, string> = {
  [PENDING_STATUS]: colors.feedback.alert300,
  [IN_PROGRESS_STATUS]: colors.important.main,
  [FINISHED_STATUS]: colors.feedback.success300,
  [ERROR_STATUS]: colors.feedback.error300,
  [STOPPED_STATUS]: colors.grays.neutral66,
};

export interface UseProcessHistoryListResult {
  rows: ProcessHistoryItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<ProcessHistoryItem>[];
  actions: TableAction<ProcessHistoryItem>[];
  filterConfig: SimpleFilter[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  isExporting: boolean;
  filters: ProcessHistoryFilters;
  setFilters: (
    filters: ProcessHistoryFilters | ((prev: ProcessHistoryFilters) => ProcessHistoryFilters),
  ) => void;
  handleFilterChange: (key: string, value: string | string[]) => void;
  refetch: () => void;
}

export function useProcessHistoryList(): UseProcessHistoryListResult {
  const { showApiError, openSidePanel } = useUI();
  const { t: translate } = useTranslation();
  const { canView } = usePagePermissions();

  const { onExport: downloadErrorReport, isExporting } = useExport<number>({
    exportFn: (historyProcessId) =>
      recalculationMonthlyCutsService.downloadErrorReport(historyProcessId),
    successMessage: translate('attendance:recalculo.historicoProcesos.messages.exportSuccess'),
    errorMessage: translate('attendance:recalculo.historicoProcesos.messages.exportError'),
  });

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<ProcessHistoryFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<ProcessHistoryItem> | null> => {
      const response = await recalculationMonthlyCutsService.getProcessHistory(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          status: params.filters.status,
          orderBy: params.sort ? ORDER_BY_MAP[params.sort.key] : undefined,
          orderDirection: params.sort?.direction,
        },
        signal,
      );

      if (response.error?.code === 'ABORTED') return null;

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:recalculo.historicoProcesos.messages.loadError'));
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    refetch,
  } = usePaginatedList<ProcessHistoryItem, ProcessHistoryFilters>({
    fetchFn,
    initialFilters: { status: null },
    initialSort: { key: 'executionDate', direction: 'DESC' },
    initialPageSize: 10,
    orderByMap: ORDER_BY_MAP,
    screenCode: ROUTE_DEFINITIONS.RECALCULO_ASISTENCIA.code,
  });

  const columns: TableColumn<ProcessHistoryItem>[] = useMemo(() => [
    {
      key: 'processId',
      header: translate('attendance:recalculo.historicoProcesos.columns.processId'),
      sortable: true,
    },
    {
      key: 'cutYear',
      header: translate('attendance:recalculo.historicoProcesos.columns.cutYear'),
      sortable: true,
    },
    {
      key: 'cutMonth',
      header: translate('attendance:recalculo.historicoProcesos.columns.cutMonth'),
      sortable: true,
    },
    {
      key: 'totalEmployees',
      header: translate('attendance:recalculo.historicoProcesos.columns.employees'),
      sortable: true,
    },
    {
      key: 'processedEmployees',
      header: translate('attendance:recalculo.historicoProcesos.columns.processed'),
      sortable: true,
    },
    {
      key: 'executionDate',
      header: translate('attendance:recalculo.historicoProcesos.columns.executionDate'),
      sortable: true,
    },
    {
      key: 'executedBy',
      header: translate('attendance:recalculo.historicoProcesos.columns.executedBy'),
      sortable: true,
    },
    {
      key: 'status',
      header: translate('attendance:recalculo.historicoProcesos.columns.status'),
      sortable: true,
      render: (row) => (
        <span style={{
          color: STATUS_COLOR_MAP[row.status] ?? colors.grays.neutral66,
          fontWeight: 500,
        }}
        >
          {row.status}
        </span>
      ),
    },
  ], [translate]);

  const actions = useMemo<TableAction<ProcessHistoryItem>[]>(() => {
    if (!canView) return [];

    return [
      {
        id: 'detail',
        label: translate('common:tableActions.detail'),
        icon: 'eye',
        onClick: (row) => openSidePanel({
          title: translate('attendance:recalculo.detalleProceso.title'),
          component: 'RecalculoDetailPanel',
          props: {
            processData: {
              processId: row.processId,
              cutYear: row.cutYear,
              cutMonth: row.cutMonth,
              cutDescription: row.cutDescription,
              processedCompanies: row.processedCompanies,
              totalEmployees: row.totalEmployees,
              processedEmployees: row.processedEmployees,
              executionDate: row.executionDate,
              completionDate: row.completionDate,
              executedBy: row.executedBy,
              status: row.status,
              stoppedBy: row.stoppedBy,
            } satisfies RecalculoProcessDetail,
          },
        }),
        hidden: (row) =>
          row.status !== ERROR_STATUS
          && row.status !== FINISHED_STATUS
          && row.status !== STOPPED_STATUS,
      },
      {
        id: 'download',
        label: translate('common:tableActions.download'),
        icon: 'export',
        onClick: (row) => { void downloadErrorReport(row.historyProcessId); },
        hidden: (row) => row.status !== ERROR_STATUS && row.status !== STOPPED_STATUS,
        disabled: () => isExporting,
      },
    ];
  }, [canView, translate, openSidePanel, downloadErrorReport, isExporting]);

  const filterConfig: SimpleFilter[] = useMemo(() => [
    {
      key: 'status',
      label: translate('attendance:recalculo.historicoProcesos.statusFilter.label'),
      placeholder: translate('attendance:recalculo.historicoProcesos.statusFilter.placeholder'),
      options: [
        { value: '', label: translate('attendance:recalculo.historicoProcesos.statusFilter.all') },
        {
          value: FINISHED_STATUS,
          label: translate('attendance:recalculo.historicoProcesos.estados.finalizado'),
        },
        {
          value: ERROR_STATUS,
          label: translate('attendance:recalculo.historicoProcesos.estados.error'),
        },
        {
          value: STOPPED_STATUS,
          label: translate('attendance:recalculo.historicoProcesos.estados.detenido'),
        },
      ],
    },
  ], [translate]);

  const handleFilterChange = useCallback((key: string, value: string | string[]) => {
    const normalized = Array.isArray(value) ? value[0] ?? '' : value;
    setFilters((previous) => ({
      ...previous,
      [key]: normalized === '' ? null : normalized,
    }));
  }, [setFilters]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    filterConfig,
    onSearch,
    onSortChange,
    loading,
    isExporting,
    filters,
    setFilters,
    handleFilterChange,
    refetch,
  };
}

export default useProcessHistoryList;
